import { CreateFilespaceMigrationDto, FilespaceMigrationDto, InvitationResponseDto, } from '@lucidlink/dto/web-service-api';
import { base64ToArray, cryptoPairsFromArray, decryptWithRsaBlockCipher, verifyPublicPrivateKeyPair, } from '@lucidlink/ts-crypto/crypto';
import { NotFoundError } from '#fetcher/fetch-error.types';
import { parserFromDto } from '#fetcher/parse-utils';
import { prepareMemberCommon } from '#sdk/common/index';
import { prepareMigrationInvitation } from '#sdk/common/invitation/prepare-migration-invitation';
import { getClassicApiContextHeaders, SingleFilespaceMigrationClientImpl, } from '#sdk/filespace-migrations-client';
import { TimeCachedValue } from '#sdk/utils/time-cached-value';
import { remapInvitationResponse } from '../invitation-manager/remap-invitation-response';
import { prepareFilespaceCreateCrypto } from '../prepare-filespace-create';
export const createMigrationManager = ({ workspaceId, workspaceFetcher, getGlobalFetcher, getAdminCrypto, loginToWorkspace, getClassicApiContext, }) => {
    return new MigrationManagerImpl(workspaceId, workspaceFetcher, getGlobalFetcher, getAdminCrypto, loginToWorkspace, getClassicApiContext);
};
class MigrationManagerImpl {
    workspaceId;
    workspaceFetcher;
    getGlobalFetcher;
    getAdminCrypto;
    loginToWorkspace;
    getClassicApiContext;
    migrationFetcher;
    constructor(workspaceId, workspaceFetcher, getGlobalFetcher, getAdminCrypto, loginToWorkspace, getClassicApiContext) {
        this.workspaceId = workspaceId;
        this.workspaceFetcher = workspaceFetcher;
        this.getGlobalFetcher = getGlobalFetcher;
        this.getAdminCrypto = getAdminCrypto;
        this.loginToWorkspace = loginToWorkspace;
        this.getClassicApiContext = getClassicApiContext;
        this.migrationFetcher = this.workspaceFetcher.createSubFetcher({
            path: ['invitations', 'migration'],
        });
    }
    async createMigration(newMigration) {
        const { headers, body } = await this.prepareRequestParams(newMigration);
        const createdMigration = await this.getGlobalFetcher().post(['filespace-migrations'], {
            headers,
            body,
            parser: parserFromDto(FilespaceMigrationDto),
        });
        return new SingleFilespaceMigrationClientImpl(this.getGlobalFetcher, createdMigration);
    }
    async prepareRequestParams(newMigration) {
        const adminCrypto = await this.getAdminCrypto();
        const { source, destination } = newMigration;
        const body = CreateFilespaceMigrationDto.create({
            source,
            destination: {
                ...destination,
                ...(await prepareFilespaceCreateCrypto(adminCrypto, destination.filespaceFormat)),
            },
        });
        let headers = {};
        if ('classicFilespaceId' in source) {
            headers = await getClassicApiContextHeaders(this.getClassicApiContext);
        }
        return { headers, body };
    }
    async ensureInvitation() {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        try {
            const migrationInvitation = await this.migrationInvitation.get();
            return migrationInvitation.invitation;
        }
        catch (error) {
            if (error instanceof NotFoundError) {
                const { invitation } = await prepareMigrationInvitation({
                    adminCrypto,
                    workspaceId: this.workspaceId,
                });
                await this.migrationFetcher.post([], {
                    body: invitation,
                });
                const migrationInvitation = await this.migrationInvitation.refresh();
                return migrationInvitation.invitation;
            }
            throw error;
        }
    }
    async migrateMember(email) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        const migrationInvitation = await this.migrationInvitation.get();
        const memberCommon = await prepareMemberCommon({
            workspaceId: this.workspaceId,
            invitationKeys: migrationInvitation.cryptoPairs,
        });
        await this.migrationFetcher.post(['members'], {
            body: { ...memberCommon.member, email },
        });
        return memberCommon.member.principalId;
    }
    migrationInvitation = new TimeCachedValue(async () => {
        const invitation = await this.migrationFetcher.get([], {
            parser: parserFromDto(InvitationResponseDto),
        });
        const adminCrypto = await this.getAdminCrypto();
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const invitationPrivateKey = await decryptWithRsaBlockCipher(invitation.ePrivateKeyByAdminKey, adminKeys.encryptPair.privateKey);
        const invitationPublicKey = base64ToArray(invitation.publicKey);
        const cryptoPairs = await cryptoPairsFromArray(invitationPublicKey, invitationPrivateKey);
        await verifyPublicPrivateKeyPair(cryptoPairs.encryptPair);
        const remappedInvitation = remapInvitationResponse(invitation, adminCrypto);
        return { cryptoPairs, invitation: remappedInvitation };
    });
}
